import type { FC } from 'react';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { styled } from '@compiled/react';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import Button from '@atlaskit/button/button';
import CheckIcon from '@atlaskit/icon/core/check-mark';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';

import { PRIMARY_COLORS } from '../colors.ts';
import { colorCodeToColorName, tokenToHex } from '../helpers.ts';

import { i18n } from './i18n.ts';
import type { ColorPickerButtonProps } from './types.ts';

type DropDownContentWrapperProps = {
	ref?: any;
};

type SelectedColorProps = {
	color: string | undefined;
	isOpen: boolean;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SelectedColor = styled.div<SelectedColorProps>({
	width: '16px',
	height: '16px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	border: `${token('border.width.selected')} solid ${(props: SelectedColorProps) =>
		props.isOpen
			? token('color.border.inverse')
			: props.color
				? props.color
				: token('color.border.accent.gray')}`,
	borderRadius: token('radius.full'),
	marginRight: token('space.negative.025'),
	marginLeft: token('space.025'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props: SelectedColorProps) => props.color,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorOption = styled.button<{ color: string }>({
	width: '20px',
	height: '20px',
	margin: 'auto',
	display: 'flex',
	border: `${token('border.width')} solid ${token('color.border.inverse')}`,
	alignItems: 'center',
	justifyContent: 'center',
	borderRadius: token('radius.full'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	backgroundColor: (props) => props.color,
	outlineColor: token('color.border.brand'),
	cursor: 'pointer',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ColorOptionWrapper = styled.div({
	cursor: 'pointer',
	flexGrow: 1,
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'&:hover button': {
		boxShadow: `0 0 0 0.8px ${token('color.border.bold')}`,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropDownContentWrapper = styled.div<DropDownContentWrapperProps>({
	display: 'flex',
	justifyContent: 'space-evenly',
	alignItems: 'stretch',
	width: '145px',
	height: '30px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonIconsWrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
});

const chevronDownIconWrapperStyles = xcss({
	marginRight: 'space.negative.075',
});

export const ColorPickerButton: FC<ColorPickerButtonProps> = ({
	selectedColor,
	onSelectColor,
	isOpen,
	onClose,
	onColorPickerToggle,
	userOffline,
	colorPickerComponentRef,
}) => {
	const intl = useIntl();

	const colorsArray = [
		PRIMARY_COLORS.BLUE,
		PRIMARY_COLORS.GREEN,
		PRIMARY_COLORS.YELLOW,
		PRIMARY_COLORS.RED,
		PRIMARY_COLORS.PURPLE,
	];

	const statusColors = colorsArray.map((color) => {
		const isSelected = selectedColor === color;
		const colorName = colorCodeToColorName(color);
		return (
			<ColorOptionWrapper onClick={() => onSelectColor(color)} key={`${color}-option`}>
				<ColorOption
					color={color}
					data-testid={`${tokenToHex(color)}-option`}
					aria-label={intl.formatMessage(i18n.colorOptionAriaLabel, { colorName })}
					{...(expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true)
						? { role: 'option', 'aria-selected': isSelected }
						: {})}
				>
					{isSelected && <CheckIcon label="selected" color={token('color.icon.subtle')} />}
				</ColorOption>
			</ColorOptionWrapper>
		);
	});

	useEffect(() => {
		// Focus color picker button after its poppup closes since the modal loses focus when the color popup is closed.
		// Had to use an id instead of ref to keep track of the button since a ref interferes with the trigger button ref
		// the popup component uses.
		const colorPickerButton = document.getElementById('content-state-color-picker-button');
		if (!isOpen && colorPickerButton) {
			// using a setTimeout since state change/re-render is interfering with timing of things and timeout will help force
			// the focus to happen after the re-render https://salesforce.stackexchange.com/a/366973
			setTimeout(() => {
				colorPickerButton.focus();
			}, 0);
		}
	}, [isOpen]);

	// TODO(brizvash): fix incompatible csstype of emotion and compiled
	return (
		<Popup
			isOpen={isOpen}
			onClose={onClose}
			testId="color-picker-popup"
			placement="bottom-start"
			zIndex={700}
			shouldRenderToParent
			trigger={({ ...buttonProps }) => (
				<Button
					{...buttonProps}
					id="content-state-color-picker-button"
					testId="color-picker-popup-trigger"
					isSelected={isOpen}
					isDisabled={userOffline}
					onClick={onColorPickerToggle}
					aria-label={intl.formatMessage(i18n.colorPickerButtonAriaLabel, {
						colorName: selectedColor ? colorCodeToColorName(selectedColor) : '',
					})}
					iconBefore={
						<ButtonIconsWrapper>
							<SelectedColor
								color={selectedColor || undefined}
								isOpen={isOpen}
								data-testid="selected-color"
							/>
							<Box xcss={chevronDownIconWrapperStyles}>
								<ChevronDownIcon color="currentColor" spacing="spacious" label="" size="small" />
							</Box>
						</ButtonIconsWrapper>
					}
				/>
			)}
			content={() => (
				<DropDownContentWrapper
					ref={colorPickerComponentRef as any}
					{...(expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true)
						? {
								role: 'listbox',
								'aria-label': intl.formatMessage(i18n.colorPickerListboxAriaLabel),
							}
						: {})}
				>
					{statusColors}
				</DropDownContentWrapper>
			)}
		/>
	);
};
